import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Reveal, SectionEyebrow } from '../components/ui-bits';
import { getBlog, postNewsletter } from '../lib/api';
import Seo from '../components/Seo';

export default function GreenRoom() {
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', hp: '' });
  const [subLoading, setSubLoading] = useState(false);

  useEffect(() => { getBlog().then(setPosts).catch(() => {}); }, []);

  const categories = useMemo(() => ['All', ...Array.from(new Set(posts.map(p => p.category)))], [posts]);
  const filtered = useMemo(() => posts.filter(p => {
    if (cat !== 'All' && p.category !== cat) return false;
    if (q && !(p.title + ' ' + p.excerpt).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [posts, cat, q]);

  const featured = posts.find(p => p.featured) || posts[0];
  const rest = filtered.filter(p => p.id !== featured?.id);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitNews = async (e) => {
    e.preventDefault();
    if (form.hp) return; // honeypot
    if (!form.email) return;
    setSubLoading(true);
    try {
      await postNewsletter({
        email: form.email.trim(),
        first_name: form.first_name.trim() || undefined,
        last_name: form.last_name.trim() || undefined,
      });
      toast.success('Subscribed. Welcome to The Green Room.');
      setForm({ first_name: '', last_name: '', email: '', hp: '' });
    } catch {
      toast.error('Sorry, we could not complete your subscription. Please try again.');
    } finally {
      setSubLoading(false);
    }
  };

  return (
    <div data-testid="greenroom-page" className="bg-white">
      <Seo
        title="The Green Room"
        description="Practical insight from the conversations happening inside the agency every day. Honest writing for working actors — self-tapes, auditions, showreels, headshots, career strategy and mental wellbeing."
        path="/green-room"
      />
      <section className="pt-40 pb-16 lg:pt-48 lg:pb-24 bg-[#faf8f4]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <Reveal>
            <SectionEyebrow>The Green Room</SectionEyebrow>
          </Reveal>
          <div className="grid lg:grid-cols-12 gap-12 mt-10 items-end">
            <Reveal className="lg:col-span-8" delay={120}>
              <h1 className="font-heading uppercase text-[#002b5c] text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9]">
                Writing for the<br /><span className="italic font-light">working</span> actor.
              </h1>
            </Reveal>
            <Reveal className="lg:col-span-4" delay={240}>
              <p className="text-base text-[#4a4a4a] leading-relaxed">
                Practical insight from the conversations happening inside the agency every day.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Featured — text-forward */}
      {featured && (
        <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Reveal>
            <Link to={`/green-room/${featured.slug}`} className="block group">
              <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start border-t border-black/10 pt-16">
                <div className="lg:col-span-3">
                  <p className="eyebrow">Featured</p>
                  <p className="mt-3 text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">{featured.category}</p>
                  <div className="mt-8 hairline opacity-40 hidden lg:block" />
                  <p className="mt-8 hidden lg:block text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">
                    {featured.author}<br />
                    <span className="text-[#C5A059]">·</span> {featured.read_time}
                  </p>
                </div>
                <div className="lg:col-span-9">
                  <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase text-[#002b5c] leading-[0.95] group-hover:text-[#C5A059] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="mt-8 text-lg text-[#4a4a4a] leading-relaxed max-w-2xl">{featured.excerpt}</p>
                  <div className="mt-8 lg:hidden flex items-center gap-4 text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">
                    <span>{featured.author}</span>
                    <span className="w-1 h-1 rounded-full bg-[#C5A059]" />
                    <span>{featured.read_time}</span>
                  </div>
                  <span className="mt-10 inline-flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase text-[#002b5c] gold-underline">
                    Read article <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </Link>
          </Reveal>
        </section>
      )}

      {/* Filters + Search */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 pb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 border-y border-black/10 py-6">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`gr-cat-${c}`}
                onClick={() => setCat(c)}
                className={`rounded-full px-4 py-2 text-[11px] tracking-[0.18em] uppercase transition-colors border ${
                  cat === c ? 'bg-[#002b5c] text-white border-[#002b5c]' : 'border-black/10 text-[#333] hover:border-[#002b5c]'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 bg-[#faf8f4] rounded-full px-5 py-2.5 lg:w-80">
            <Search size={16} className="text-[#6b6b6b]" />
            <input
              data-testid="gr-search"
              type="text"
              placeholder="Search articles"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-[#9a9a9a]"
            />
          </div>
        </div>
      </section>

      {/* Grid — text-only editorial cards */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {rest.map((p, i) => (
            <Reveal key={p.id} delay={(i % 6) * 60}>
              <Link
                to={`/green-room/${p.slug}`}
                className="group card-lift bg-white border border-black/8 rounded-2xl p-8 lg:p-10 h-full flex flex-col hover:border-[#C5A059]/60 hover:shadow-[0_20px_60px_-20px_rgba(0,43,92,0.15)] transition-all duration-500"
              >
                <div className="flex items-start justify-between">
                  <p className="eyebrow text-[#6b6b6b]">{p.category}</p>
                  <span className="font-heading text-[#C5A059] text-sm tracking-wider">
                    N°{String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="font-heading text-2xl md:text-[26px] uppercase text-[#002b5c] mt-8 leading-[1.05] group-hover:text-[#C5A059] transition-colors">
                  {p.title}
                </h3>
                <p className="mt-4 text-sm text-[#4a4a4a] leading-relaxed line-clamp-3">{p.excerpt}</p>
                <div className="mt-auto pt-10">
                  <div className="hairline opacity-40 mb-4" />
                  <div className="flex items-center justify-between text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">
                    <span>{p.author}</span>
                    <span>{p.read_time}</span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Newsletter */}
      <section className="bg-[#002b5c] text-white">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow text-[#C5A059]">Newsletter</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase mt-6 leading-[0.95]">
              New writing,<br /><span className="italic font-light text-[#C5A059]">monthly.</span>
            </h2>
            <p className="mt-6 max-w-lg text-white/75 leading-relaxed">
              Honest writing on the working actor's life, delivered once a month. No noise.
            </p>
          </div>
          <form onSubmit={submitNews} className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="border-b border-white/25 focus-within:border-[#C5A059] transition-colors">
                <input
                  data-testid="gr-newsletter-fname"
                  type="text"
                  autoComplete="given-name"
                  aria-label="First name"
                  placeholder="First name"
                  value={form.first_name}
                  onChange={upd('first_name')}
                  className="w-full bg-transparent py-3 outline-none text-white placeholder:text-white/40"
                />
              </div>
              <div className="border-b border-white/25 focus-within:border-[#C5A059] transition-colors">
                <input
                  data-testid="gr-newsletter-lname"
                  type="text"
                  autoComplete="family-name"
                  aria-label="Last name"
                  placeholder="Last name"
                  value={form.last_name}
                  onChange={upd('last_name')}
                  className="w-full bg-transparent py-3 outline-none text-white placeholder:text-white/40"
                />
              </div>
            </div>
            <div className="flex items-center border-b border-white/25 focus-within:border-[#C5A059] transition-colors">
              <input
                data-testid="gr-newsletter-input"
                type="email" required
                autoComplete="email"
                aria-label="Email address"
                placeholder="your@email.com"
                value={form.email}
                onChange={upd('email')}
                className="bg-transparent flex-1 py-4 outline-none text-white placeholder:text-white/40"
              />
              <button
                data-testid="gr-newsletter-submit"
                type="submit"
                disabled={subLoading}
                className="ml-4 text-[#C5A059] hover:text-white transition-colors disabled:opacity-50"
                aria-label="Subscribe to newsletter"
              >
                <ArrowRight size={20} />
              </button>
            </div>
            <p className="mt-4 text-[11px] text-white/50 leading-relaxed">
              By subscribing you consent to receive occasional emails from David Ball Management.
              Unsubscribe any time. See our{' '}
              <Link to="/privacy" className="underline decoration-white/30 hover:text-white/80">privacy notice</Link>.
            </p>
            {/* honeypot */}
            <input
              type="text"
              name="company"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form.hp}
              onChange={upd('hp')}
              className="sr-only"
            />
          </form>
        </div>
      </section>
    </div>
  );
}
